import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMap } from 'react-leaflet';
import {
    countryGridCoordinatesState,
    currentMaxZoomState,
    distanceBetweenGeneratedPointsState,
    gridRegionCoordinatesState,
    mapOptionsState,
    squareGridCoordinatesState,
} from '../../../../../app/redux/retrievers/retrievers';
import { generateGridCells } from './functions/generateGridCells';
import { minimumZoomSetter } from './functions/minimumZoomSetter';
import { addCountryLeafletGridLayer, addLeafletGridLayer } from './functions/addLeafletGridLayer';
import { addGridCellsToMap } from './functions/addGridCellsToMap';
import { removeGridCellsFromMap } from './functions/removeGridCellsFromMap';
import { generateGridForGridCellService } from './functions/generateGridForGridCellService';
import { GridCellData } from '../../../../../core/types/types';
import { sendDataToParent } from '../../../base/functions/sendDataToParent';

const SquareGridLayer = () => {
    const map = useMap();
    const dispatch = useDispatch();
    const gridLayerRef = useRef(null);

    const gridRegionPolygonCoordinates = useSelector(gridRegionCoordinatesState);
    const distanceBetweenGeneratedPoints = useSelector(distanceBetweenGeneratedPointsState);
    const squareGridCoordinates = useSelector(squareGridCoordinatesState);
    const countryGridCoordinates = useSelector(countryGridCoordinatesState);
    const currentMaxZoom = useSelector(currentMaxZoomState);
    const mapOptions = useSelector(mapOptionsState);

    const [minimumGridDisplayZoom, setMinimumGridDisplayZoom] = useState<number>(15);
    const [pickedCells, setPickedCells] = useState<GridCellData[] | []>([]);

    map.setMaxZoom(currentMaxZoom);

    /*user picked polygon grid*/
    useEffect(() => {
        if (gridRegionPolygonCoordinates && distanceBetweenGeneratedPoints && !mapOptions.gridCellService) {
            generateGridCells(dispatch, gridRegionPolygonCoordinates, distanceBetweenGeneratedPoints);
        }
    }, [gridRegionPolygonCoordinates, distanceBetweenGeneratedPoints, mapOptions]);

    /*grid cell service*/
    useEffect(() => {
        if (mapOptions.gridCellService) {
            generateGridForGridCellService(dispatch);
        }
    }, [mapOptions]);

    useEffect(() => {
        if (gridLayerRef && gridLayerRef.current) {
            removeGridCellsFromMap(map, gridLayerRef);
        }
    }, [distanceBetweenGeneratedPoints]);

    /*display user choses polygon grid*/
    useEffect(() => {
        if (squareGridCoordinates) {
            addLeafletGridLayer(gridLayerRef, squareGridCoordinates);
        } else if (!squareGridCoordinates && gridLayerRef && gridLayerRef.current) {
            removeGridCellsFromMap(map, gridLayerRef);
        }
    }, [squareGridCoordinates]);

    /*display country grid*/
    useEffect(() => {
        if (countryGridCoordinates) {
            addCountryLeafletGridLayer(gridLayerRef, countryGridCoordinates, setPickedCells);
        } else if (!countryGridCoordinates && gridLayerRef && gridLayerRef.current) {
            removeGridCellsFromMap(map, gridLayerRef);
        }
    }, [countryGridCoordinates]);

    useEffect(() => {
        const updateGridCells = () => {
            if (map.getZoom() >= minimumGridDisplayZoom && gridLayerRef.current) {
                addGridCellsToMap(map, gridLayerRef);
            } else {
                removeGridCellsFromMap(map, gridLayerRef);
            }
        };
        if (squareGridCoordinates || countryGridCoordinates) {
            map.on('moveend', updateGridCells);
            map.on('zoomend', updateGridCells);
            return () => {
                map.off('moveend', updateGridCells);
                map.off('zoomend', updateGridCells);
            };
        }
    }, [map, countryGridCoordinates, squareGridCoordinates, minimumGridDisplayZoom]);

    useEffect(() => {
        if (squareGridCoordinates) minimumZoomSetter(distanceBetweenGeneratedPoints, setMinimumGridDisplayZoom);
        if (countryGridCoordinates) minimumZoomSetter(5000, setMinimumGridDisplayZoom);
    }, [countryGridCoordinates, squareGridCoordinates, distanceBetweenGeneratedPoints]);

    /*send picked grid service cells to parent*/
    useEffect(() => {
        sendDataToParent({
            type: 'picked-grid-cells',
            pickedCells,
        });
    }, [pickedCells]);

    return null;
};

export default SquareGridLayer;
